/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * T-1: keep the website's sign-in state honest about the forum.
 *
 * Website sign-in state is a token in local storage. Forum access is an
 * HttpOnly cookie the browser will not let script read. Nothing on the
 * marketing site ever asked the backend whether that cookie was still valid, so
 * a customer who signed out on the forum kept seeing My Account on the website
 * indefinitely.
 *
 * This hook asks the backend, and the backend answers with one boolean. Every
 * eligibility rule lives there because the cookie cannot be inspected here.
 *
 * The mint flag is the part that prevents a serious fault. Without it, a
 * signed-in customer who has a forum display name but has never opened the
 * forum has no cookie, would be reported as not synced, and would be signed out
 * of the website they are actively using. The flag makes the absence of a
 * cookie mean something only when a cookie was expected.
 */

import { useEffect } from "react";
import { configuration } from "@/lib/configuration";
import { authClient, getSessionToken } from "@/lib/auth-client";
import { hasForumCookieMinted } from "@/lib/forum-access";

/**
 * T-1 correction 3: every return to the tab used to issue a network request, so
 * a customer switching tabs frequently generated one each time. Thirty seconds
 * is ample - a forum sign-out that takes half a minute to be noticed is not a
 * problem worth a request per focus change.
 *
 * Module scope rather than a ref: the limit is per browser tab, not per mounted
 * component, and the header can remount during navigation.
 */
const MIN_SYNC_INTERVAL_MS = 30_000;
let lastSyncAt = 0;

/** Exported for tests, which would otherwise inherit the previous test's clock. */
export function resetForumSyncThrottle(): void {
  lastSyncAt = 0;
}

/**
 * IND-12: a sign-out that discards half a support form is a worse outcome than
 * a header that is briefly out of date.
 *
 * A customer filling in a support form, who switched tabs to check something,
 * comes back to a signed-out header - and if any part of that form depends on
 * the signed-in state, their entries are gone. The sign-out itself is correct
 * behaviour; only the timing is unkind, so it is deferred to the next
 * visibility change rather than cancelled. The customer is not doing anything
 * privileged in the meantime.
 */
function hasUnsavedFormInput(): boolean {
  return document.querySelector("form [data-dirty='true']") !== null;
}

export function useForumSessionSync(): void {
  useEffect(() => {
    let cancelled = false;

    const sync = async (): Promise<void> => {
      const now = Date.now();
      if (now - lastSyncAt < MIN_SYNC_INTERVAL_MS) return;
      lastSyncAt = now;

      const sessionToken = getSessionToken();
      if (!sessionToken) return;

      // Only ask when a cookie was actually minted for this browser. See the
      // note above - this is what stops the check signing out every
      // forum-eligible customer who has never opened the forum.
      if (!hasForumCookieMinted()) return;

      let data: { forum_session_enabled?: boolean; synced?: boolean };
      try {
        const res = await fetch(
          `${configuration.backendURL}/api/auth/forum-session/status`,
          {
            method: "GET",
            headers: { "X-Session-Token": sessionToken },
            credentials: "include",
          },
        );
        if (!res.ok) return;
        data = await res.json();
      } catch {
        // A failed request is not evidence of a sign-out. Staying signed in is
        // the safe answer when the answer is unknown.
        return;
      }

      if (cancelled) return;
      if (!data.forum_session_enabled || data.synced !== false) return;

      if (hasUnsavedFormInput()) return; // IND-12

      // T-1 correction 1: no page reload. signOut() clears the stored token and
      // the header reads that state through React, so it updates in place. A
      // full reload is heavier than the situation needs and happens without
      // warning - the customer is midway through a page, the tab regains focus,
      // and the page reloads under them. If they were filling in a form, the
      // entries are gone. If any component genuinely needs a reload to reflect
      // signed-out state, that component should be fixed rather than the whole
      // page reloaded to hide it.
      await authClient.signOut();
    };

    void sync();

    const onVisible = (): void => {
      if (document.visibilityState === "visible") void sync();
    };

    // T-1 correction 2: visibilitychange fires when a tab is switched back to.
    // It does NOT fire when a page is restored from the back-forward cache,
    // which is exactly what happens when a customer signs out on the forum and
    // presses Back - one of the commonest routes into the state this hook
    // exists to detect.
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) void sync();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);
}
